import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { ButtonWithPopoverProps } from './ButtonWithPopover.types';

const bgStyles = ['tw:bg-sq-white', 'tw:dark:bg-sq-dark-background'].join(' ');

const borderStyles = [
  'tw:border-solid',
  'tw:border',
  'tw:rounded-[0.5rem]',
  'tw:border-sq-disabled-gray',
  'tw:dark:border-gray-500',
].join(' ');

const disabledClasses = ['tw:opacity-50', 'tw:cursor-not-allowed'].join(' ');

const ButtonWithPopover = ({
  children,
  trigger,
  id,
  hasArrow,
  extraTriggerClassNames,
  extraPopoverClassNames,
  containerTestId,
  onInteractOutside,
  onPointerDownOutside,
  isTriggerAsChild = false,
  shouldTriggerFullWidth = false,
  disabled = false,
  align = 'end',
  alignOffset = -35,
  placement = 'bottom',
  placementOffset = 2,
  isHoverEnabled = false,
  hoverOpenDelay = 300,
  onOpenChange,
  isOpen,
  isCloseOnContentClick = false,
  isPortal = false,
  ...tooltipProps
}: ButtonWithPopoverProps) => {
  const tooltipData = getQTipData(tooltipProps);
  const timeout = useRef<null | NodeJS.Timeout>(null);
  const [isHoveredOpen, setIsHoveredOpen] = useState<boolean>(false);
  const clearHoverTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
  };

  const onHoverStart = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(true);
        onOpenChange && onOpenChange(true);
      }, hoverOpenDelay);
    }
  };

  const onHoverEnd = () => {
    if (isHoverEnabled) {
      clearHoverTimeout();
      timeout.current = setTimeout(() => {
        setIsHoveredOpen(false);
        onOpenChange && onOpenChange(false);
      }, 200);
    }
  };

  useEffect(() => clearHoverTimeout, []);

  const renderContent = () => {
    return (
      <PopoverPrimitive.Content
        sideOffset={placementOffset}
        align={align}
        side={placement}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        alignOffset={isHoverEnabled ? -10 : alignOffset}
        asChild
        onClick={() => isCloseOnContentClick && onOpenChange && onOpenChange(false)}
        className="tw:focus-visible:outline-none tw:outline-none tw:w-full tw:h-full">
        <div
          data-testid={containerTestId}
          className={`${bgStyles} ${borderStyles} tw:relative tw:z-[1200] tw:min-w-6 tw:focus-visible:outline-none tw:outline-none tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out
         tw:data-[side=top]:animate-slideDownAndFade tw:data-[side=right]:animate-slideLeftAndFade tw:data-[side=bottom]:animate-slideUpAndFade tw:data-[side=left]:animate-slideRightAndFade tw:text-sq-text-color
        ${extraPopoverClassNames || ''}`}>
          {hasArrow && (
            <PopoverPrimitive.Arrow asChild>
              <div className="tw:fill-transparent tw:bg-sq-white tw:w-sq-15 tw:h-sq-15 tw:-mt-sq-7 tw:rotate-45 tw:dark:bg-sq-dark-background tw:border-b tw:border-r tw:border-sq-disabled-gray tw:dark:border-gray-500" />
            </PopoverPrimitive.Arrow>
          )}
          {children}
        </div>
      </PopoverPrimitive.Content>
    );
  };

  const renderPopover = (popoverOpenState?: boolean) => {
    return (
      <PopoverPrimitive.Root open={popoverOpenState} defaultOpen={false} onOpenChange={onOpenChange}>
        {trigger ? (
          <PopoverPrimitive.Trigger
            id={id}
            asChild={isTriggerAsChild}
            className={`tw:focus-visible:outline-none tw:outline-none tw:bg-transparent ${
              shouldTriggerFullWidth ? 'tw:w-full tw:h-full' : ''
            }`}
            disabled={disabled}>
            <div
              {...tooltipData}
              onMouseOver={onHoverStart}
              onMouseOut={onHoverEnd}
              className={`tw:focus-visible:outline-none tw:outline-none tw:dark:text-sq-white ${
                disabled ? disabledClasses : ''
              } ${shouldTriggerFullWidth ? 'tw:flex tw:flex-col tw:items-center tw:justify-center' : 'tw:inline'} ${
                extraTriggerClassNames || ''
              }`}>
              {trigger}
            </div>
          </PopoverPrimitive.Trigger>
        ) : (
          <PopoverPrimitive.Trigger asChild>
            <div />
          </PopoverPrimitive.Trigger>
        )}
        {isPortal ? <PopoverPrimitive.Portal>{renderContent()}</PopoverPrimitive.Portal> : renderContent()}
      </PopoverPrimitive.Root>
    );
  };

  return isHoverEnabled ? renderPopover(isHoveredOpen) : renderPopover(isOpen);
};

export default ButtonWithPopover;
